




































































import { namespace } from 'vuex-class';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { CART } from '@/store/types';
import { UnitDefinition } from '@/services/ProductDeserializer';
import UnitSelector from '@/common/UnitSelector.vue';
import AmountSelector from '@/common/AmountSelector.vue';
import OrderTemplateSelection from '@/common/OrderTemplateSelection.vue';
import LabelSelection from '@/common/LabelSelection.vue';
import { OrderTemplateDto, PriceDto, ProductDto } from '@eurogast/hub-client-typescript';
import { LockService } from '@/services/LockService';
import { ApiService } from '@/services/ApiService';
import ProductDtoComponentBase from '@/common/ProductDtoComponentBase';
import { Cart, CartItem } from '@/services/cart/model/cart';
import { DependencyService } from '@/services/DependencyService';
import {LinkUtil} from '@/services/LinkUtil';
import AddToCartButton from '@/common/cart-button/AddToCartButton.vue';

const cart = namespace(CART);

@Component({ components: {AddToCartButton, AmountSelector, UnitSelector, OrderTemplateSelection, LabelSelection } })
export default class FeaturedProduct extends ProductDtoComponentBase {
  @Prop({ required: true })
  public product: ProductDto;
  @Prop({ required: false })
  public bulkPrices: PriceDto[];
  @Prop({ required: false, default: () => [] })
  public orderTemplates: OrderTemplateDto[];
  @Prop({ required: false, default: false })
  public labelSelection: boolean;

  @cart.State('cart')
  public cart: Cart;
  public inCart = false;

  public currentAmount = Math.max(this.minSaleQuantity, 1);

  private cartService = DependencyService.getCartService();
  private lockService: LockService;
  private unitWatcher;
  private amountWatcher;
  private deletePending = false;
  private currentAmountString = Math.max(this.minSaleQuantity, 1).toString();

  get visibleUnits(): UnitDefinition[] {
    return this.product.units.filter((unit) => unit.isVisible)
      .slice(0, 3); // little horizontal space available
  }

  get minSaleQuantity(): number {
    return this.product.minSaleQuantity > 0 ? this.product.minSaleQuantity : 1;
  }

  get showSkuInSubTitle(): boolean {
    return false;
  }

  get cartItem(): CartItem {
    const unit = this.currentUnit;
    return CartItem.buildWithUnit(
        this.product.sku,
        this.currentAmount,
        unit
    );
  }

  get isPurchasable(): boolean {
    return this.product.availability === ProductDto.AvailabilityEnum.ACTIVE;
  }

  get showSelectors(): boolean {
    return this.isLoggedIn && this.isPurchasable;
  }

  get disabledCartButtonUrl(): string {
    if (!this.isPurchasable) {
      return LinkUtil.getProductDetailUrl(this.product.name, this.product.sku);
    }

    return '';
  }

  public getMinimumSalesQuantity() {
    return this.minSaleQuantity / this.currentUnit.ratio;
  }

  @Watch('cart')
  public onCartChange() {
    if (!this.cart) {
      return;
    }

    if (this.unitWatcher) {
      this.unitWatcher();
    }
    if (this.amountWatcher) {
      this.amountWatcher();
    }

    const existingItem = this.cartService.getItem(this.product.sku);
    if (existingItem) {
      this.currentAmount = existingItem.amount;
      this.currentUnit = this.product.units.find((unit) => unit.abbreviation === existingItem.unitAbbreviation);
      this.inCart = true; // set last to avoid change handlers saving the item
      this.unitWatcher = this.$watch('currentUnit', this.onUnitChange);
      this.amountWatcher = this.$watch('currentAmount', this.onAmountChange);
    } else {
      this.deletePending = false;
      this.inCart = false;
    }
  }

  public onAmountChange(newValue, oldValue) {
    this.putItemIfChanged(newValue, oldValue);
  }

  public onUnitChange(newValue, oldValue) {
    if (this.currentAmount * newValue.ratio < this.minSaleQuantity) {
      this.currentAmount = Math.ceil(this.minSaleQuantity / newValue.ratio);
    }
    this.putItemIfChanged(newValue, oldValue);
  }

  public onAddToCart() {
    if (this.isPurchasable) {
      this.addToCart();
    }
  }

  public addToCart() {
    if (!this.isValidAmount(this.currentAmountString)) {
      const minQuantity = Math.max(Math.ceil(this.getMinimumSalesQuantity()), 1);
      ApiService.defaultInfoHandler(this.$t('cms.featuredProduct.invalidAmountForProduct',
        { sku: this.product.sku, productName: this.product.name, minQty: minQuantity }).toString()
      );
      return;
    }
    this.cartService.putItem(this.cartItem);
  }

  public removeFromCart() {
    if (this.inCart && !this.deletePending) {
      this.deletePending = true;
      this.cartService.removeItem(this.cartItem);
    }
  }

  public mounted() {
    super.mounted();
    this.lockService = new LockService(this.beforeUnload.bind(this), 1000);
    this.currentAmount = Math.ceil(this.minSaleQuantity / this.currentUnit.ratio);
    this.onCartChange();
  }

  public beforeUnload() {
    this.cartService.putItem(this.cartItem, true);
  }

  public getUnitForOrderTemplate() {
    return this.visibleUnits[0];
  }

  private putItemIfChanged(newValue, oldValue) {
    if (newValue !== oldValue && this.inCart) {
      this.cartService.putItemWithLock(this.cartItem, this.lockService);
    }
  }

  private setCurrentAmountString(argument) {
    this.currentAmountString = argument;
    if (this.isValidAmount(argument)) {
      this.currentAmount = argument;
    }
  }
}
